import { useState, useEffect } from "react"
import Chip from "@mui/material/Chip"
import CloudUploadIcon from "@mui/icons-material/CloudUpload"
import { styled } from "@mui/material/styles"
import Swal from "sweetalert2"

import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"
import MDButton from "components/MDButton"

import { uploadImage, fetchImageBase64, resetImages, IMAGE_TYPES } from "monitor/Configurator/service"

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
})

function SiteImagesSection({ openConfigurator = true, companyName, companyId, showTitle = true }) {
  const normalizedCompanyId = companyId === null || companyId === undefined ? "" : String(companyId).trim()
  const hasCompanyId = Boolean(normalizedCompanyId)
  const [backgroundFile, setBackgroundFile] = useState(null)
  const [backgroundPreview, setBackgroundPreview] = useState(null)
  const [backgroundExistingPreview, setBackgroundExistingPreview] = useState(null)
  const [backgroundError, setBackgroundError] = useState("")
  const [backgroundUploaded, setBackgroundUploaded] = useState(false)
  const [backgroundUploading, setBackgroundUploading] = useState(false)

  const [logoFile, setLogoFile] = useState(null)
  const [logoPreview, setLogoPreview] = useState(null)
  const [logoExistingPreview, setLogoExistingPreview] = useState(null)
  const [logoError, setLogoError] = useState("")
  const [logoUploaded, setLogoUploaded] = useState(false)
  const [logoUploading, setLogoUploading] = useState(false)

  const [footerFile, setFooterFile] = useState(null)
  const [footerPreview, setFooterPreview] = useState(null)
  const [footerExistingPreview, setFooterExistingPreview] = useState(null)
  const [footerError, setFooterError] = useState("")
  const [footerUploaded, setFooterUploaded] = useState(false)
  const [footerUploading, setFooterUploading] = useState(false)

  const [defaultResetting, setDefaultResetting] = useState(false)
  const [uploadSummary, setUploadSummary] = useState("")
  const [uploadSummaryColor, setUploadSummaryColor] = useState("info")

  const clearLocalImageSelections = () => {
    if (backgroundPreview) URL.revokeObjectURL(backgroundPreview)
    if (logoPreview) URL.revokeObjectURL(logoPreview)
    if (footerPreview) URL.revokeObjectURL(footerPreview)

    setBackgroundFile(null)
    setBackgroundPreview(null)
    setBackgroundError("")
    setBackgroundUploaded(false)
    setBackgroundUploading(false)

    setLogoFile(null)
    setLogoPreview(null)
    setLogoError("")
    setLogoUploaded(false)
    setLogoUploading(false)

    setFooterFile(null)
    setFooterPreview(null)
    setFooterError("")
    setFooterUploaded(false)
    setFooterUploading(false)
  }

  const getBase64FromPayload = (payload) => {
    if (!payload) return null
    if (typeof payload === "string") return payload

    if (Array.isArray(payload)) {
      for (const item of payload) {
        const nested = getBase64FromPayload(item)
        if (nested) return nested
      }
      return null
    }

    if (typeof payload !== "object") return null

    const candidateKeys = ["base64", "imageBase64", "data", "content", "value", "file"]
    for (const key of candidateKeys) {
      if (typeof payload[key] === "string" && payload[key].trim()) {
        return payload[key]
      }
    }

    for (const value of Object.values(payload)) {
      const nested = getBase64FromPayload(value)
      if (nested) return nested
    }

    return null
  }

  const toImageDataUrl = (payload) => {
    const rawValue = getBase64FromPayload(payload)
    if (!rawValue) return null

    const trimmed = rawValue.trim()
    if (trimmed.startsWith("data:image/")) {
      return trimmed
    }

    const normalized = trimmed.replace(/\s/g, "")
    const mimeType = normalized.startsWith("/9j/")
      ? "image/jpeg"
      : normalized.startsWith("R0lGOD")
        ? "image/gif"
        : normalized.startsWith("PHN2Zy")
          ? "image/svg+xml"
          : "image/png"

    return `data:${mimeType};base64,${normalized}`
  }

  const loadExistingImages = async () => {
    if (!hasCompanyId) {
      setBackgroundExistingPreview(null)
      setLogoExistingPreview(null)
      setFooterExistingPreview(null)
      return
    }

    try {
      const [backgroundPayload, logoPayload, footerPayload] = await Promise.all([
        fetchImageBase64(IMAGE_TYPES.BACKGROUND, normalizedCompanyId),
        fetchImageBase64(IMAGE_TYPES.BRAND, normalizedCompanyId),
        fetchImageBase64(IMAGE_TYPES.FOOTER, normalizedCompanyId),
      ])

      setBackgroundExistingPreview(toImageDataUrl(backgroundPayload))
      setLogoExistingPreview(toImageDataUrl(logoPayload))
      setFooterExistingPreview(toImageDataUrl(footerPayload))
    } catch {
      setBackgroundExistingPreview(null)
      setLogoExistingPreview(null)
      setFooterExistingPreview(null)
    }
  }

  useEffect(() => {
    if (!openConfigurator || !hasCompanyId) return
    clearLocalImageSelections()
    setUploadSummary("")
    loadExistingImages()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openConfigurator, hasCompanyId, normalizedCompanyId])

  useEffect(() => {
    if (hasCompanyId) return

    clearLocalImageSelections()
    setBackgroundExistingPreview(null)
    setLogoExistingPreview(null)
    setFooterExistingPreview(null)
    setUploadSummary("")
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasCompanyId])

  useEffect(
    () => () => {
      if (backgroundPreview) {
        URL.revokeObjectURL(backgroundPreview)
      }
      if (logoPreview) {
        URL.revokeObjectURL(logoPreview)
      }
      if (footerPreview) {
        URL.revokeObjectURL(footerPreview)
      }
    },
    [backgroundPreview, logoPreview, footerPreview]
  )

  const getImageSize = (file) =>
    new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file)
      const image = new Image()

      image.onload = () => {
        resolve({ width: image.width, height: image.height, url })
      }

      image.onerror = () => {
        URL.revokeObjectURL(url)
        reject(new Error("No se pudo leer la imagen seleccionada"))
      }

      image.src = url
    })

  const handleImageUpload = async ({
    event,
    expectedWidth,
    expectedHeight,
    setFile,
    setPreview,
    setError,
    setUploaded,
  }) => {
    if (!hasCompanyId) {
      event.target.value = ""
      return
    }

    const file = event.target.files?.[0]
    setUploadSummary("")

    if (!file) {
      return
    }

    if (!file.type.startsWith("image/")) {
      setError("Solo se permiten archivos de imagen.")
      setFile(null)
      setPreview(null)
      setUploaded(false)
      event.target.value = ""
      return
    }

    let objectUrl = null

    try {
      const { width, height, url } = await getImageSize(file)
      objectUrl = url
      const isValidSize = width <= expectedWidth && height <= expectedHeight

      if (!isValidSize) {
        setError(`Tamano maximo: ${expectedWidth}x${expectedHeight} px.\nSeleccionaste: ${width}x${height} px.`)
        URL.revokeObjectURL(url)
        setFile(null)
        setPreview(null)
        setUploaded(false)
        event.target.value = ""
        return
      }

      setError("")
      setFile(file)
      setUploaded(false)
      setPreview((previous) => {
        if (previous) {
          URL.revokeObjectURL(previous)
        }
        return url
      })
      event.target.value = ""
    } catch (error) {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl)
      }
      setFile(null)
      setPreview(null)
      setUploaded(false)
      event.target.value = ""
      setError(error?.message || "No se pudo procesar la imagen seleccionada.")
    }
  }

  const handleBackgroundUpload = (event) =>
    handleImageUpload({
      event,
      expectedWidth: 2400,
      expectedHeight: 1600,
      setFile: setBackgroundFile,
      setPreview: setBackgroundPreview,
      setError: setBackgroundError,
      setUploaded: setBackgroundUploaded,
    })

  const handleLogoUpload = (event) =>
    handleImageUpload({
      event,
      expectedWidth: 960,
      expectedHeight: 960,
      setFile: setLogoFile,
      setPreview: setLogoPreview,
      setError: setLogoError,
      setUploaded: setLogoUploaded,
    })

  const handleFooterUpload = (event) =>
    handleImageUpload({
      event,
      expectedWidth: 184,
      expectedHeight: 30,
      setFile: setFooterFile,
      setPreview: setFooterPreview,
      setError: setFooterError,
      setUploaded: setFooterUploaded,
    })

  const invalidateBackgroundImageCache = () => {
    localStorage.removeItem("app.backgroundImage.dataUrl")
    localStorage.removeItem("app.backgroundImage.ts")
  }

  const invalidateBrandLogoCache = () => {
    localStorage.removeItem("app.brandLogo.dataUrl")
    localStorage.removeItem("app.brandLogo.ts")
  }

  const invalidateFooterLogoCache = () => {
    localStorage.removeItem("app.footerLogo.dataUrl")
    localStorage.removeItem("app.footerLogo.ts")
  }

  const isAnyUploading = backgroundUploading || logoUploading || footerUploading || defaultResetting
  const controlsDisabled = isAnyUploading || !hasCompanyId
  const hasPendingUploads = Boolean(
    (backgroundFile && !backgroundUploaded) ||
    (logoFile && !logoUploaded) ||
    (footerFile && !footerUploaded)
  )
  const backgroundPreviewToShow = backgroundPreview || backgroundExistingPreview
  const logoPreviewToShow = logoPreview || logoExistingPreview
  const footerPreviewToShow = footerPreview || footerExistingPreview

  const handleUploadPendingImages = async () => {
    if (!hasCompanyId) {
      return
    }

    setUploadSummary("")

    const pendingItems = [
      {
        file: backgroundFile,
        uploaded: backgroundUploaded,
        type: IMAGE_TYPES.BACKGROUND,
        setUploaded: setBackgroundUploaded,
        setError: setBackgroundError,
        setUploading: setBackgroundUploading,
      },
      {
        file: logoFile,
        uploaded: logoUploaded,
        type: IMAGE_TYPES.BRAND,
        setUploaded: setLogoUploaded,
        setError: setLogoError,
        setUploading: setLogoUploading,
      },
      {
        file: footerFile,
        uploaded: footerUploaded,
        type: IMAGE_TYPES.FOOTER,
        setUploaded: setFooterUploaded,
        setError: setFooterError,
        setUploading: setFooterUploading,
      },
    ].filter((item) => item.file && !item.uploaded)

    if (pendingItems.length === 0) {
      setUploadSummaryColor("info")
      setUploadSummary("No hay imagenes pendientes para subir.")
      return
    }

    let successCount = 0
    let failedCount = 0

    for (const item of pendingItems) {
      try {
        item.setUploading(true)
        await uploadImage(item.type, item.file, normalizedCompanyId)
        item.setUploaded(true)
        item.setError("")
        if (item.type === IMAGE_TYPES.BACKGROUND) {
          invalidateBackgroundImageCache()
          window.dispatchEvent(new Event("background-image:refresh"))
        }
        if (item.type === IMAGE_TYPES.BRAND) {
          invalidateBrandLogoCache()
          window.dispatchEvent(new Event("brand-logo:refresh"))
        }
        if (item.type === IMAGE_TYPES.FOOTER) {
          invalidateFooterLogoCache()
          window.dispatchEvent(new Event("footer-logo:refresh"))
        }
        successCount += 1
      } catch (error) {
        item.setError(error?.message || "No se pudo subir la imagen.")
        failedCount += 1
      } finally {
        item.setUploading(false)
      }
    }

    if (failedCount === 0) {
      setUploadSummaryColor("success")
      setUploadSummary(`Se subieron ${successCount} imagen(es).`)
      return
    }

    if (successCount === 0) {
      setUploadSummaryColor("error")
      setUploadSummary("No se pudo subir ninguna imagen.")
      return
    }

    setUploadSummaryColor("warning")
    setUploadSummary(`Subidas: ${successCount}. Fallidas: ${failedCount}.`)
  }

  const handleResetPendingImages = () => {
    let resetCount = 0

    if (backgroundFile && !backgroundUploaded) {
      if (backgroundPreview) URL.revokeObjectURL(backgroundPreview)
      setBackgroundFile(null)
      setBackgroundPreview(null)
      setBackgroundError("")
      setBackgroundUploaded(false)
      setBackgroundUploading(false)
      resetCount += 1
    }

    if (logoFile && !logoUploaded) {
      if (logoPreview) URL.revokeObjectURL(logoPreview)
      setLogoFile(null)
      setLogoPreview(null)
      setLogoError("")
      setLogoUploaded(false)
      setLogoUploading(false)
      resetCount += 1
    }

    if (footerFile && !footerUploaded) {
      if (footerPreview) URL.revokeObjectURL(footerPreview)
      setFooterFile(null)
      setFooterPreview(null)
      setFooterError("")
      setFooterUploaded(false)
      setFooterUploading(false)
      resetCount += 1
    }

    if (resetCount === 0) {
      setUploadSummaryColor("info")
      setUploadSummary("No hay imagenes pendientes para limpiar.")
      return
    }

    setUploadSummaryColor("info")
    setUploadSummary(`Se limpiaron ${resetCount} imagen(es) pendientes.`)
  }

  const clearAllImageState = () => {
    clearLocalImageSelections()
    setBackgroundExistingPreview(null)
    setLogoExistingPreview(null)
    setFooterExistingPreview(null)
  }

  const handleDefaultImages = async () => {
    if (!hasCompanyId) {
      return
    }

    const confirm = await Swal.fire({
      title: "Confirmar restauracion",
      text: "Esta accion eliminara las imagenes actuales.\r\n Estas seguro?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Si, restaurar",
      cancelButtonText: "Cancelar",
    })

    if (!confirm.isConfirmed) {
      return
    }

    setUploadSummary("")
    setDefaultResetting(true)
    try {
      await resetImages(normalizedCompanyId)
      invalidateBackgroundImageCache()
      invalidateBrandLogoCache()
      invalidateFooterLogoCache()
      window.dispatchEvent(new Event("background-image:refresh"))
      window.dispatchEvent(new Event("brand-logo:refresh"))
      window.dispatchEvent(new Event("footer-logo:refresh"))
      clearAllImageState()
      setUploadSummaryColor("success")
      setUploadSummary("Imagenes restauradas con exito.")
    } catch (error) {
      setUploadSummaryColor("error")
      setUploadSummary(error?.message || "No se pudieron restaurar las imagenes por defecto.")
    } finally {
      setDefaultResetting(false)
    }
  }

  return (
    <>
      <MDBox mt={2}>
        <MDBox mb={2}>
          {showTitle && <MDTypography variant="h5" color="text">
            {companyName}
          </MDTypography>}
          <MDTypography variant="body2" color="text">
            Imagenes del sitio.
          </MDTypography>
        </MDBox>
        <MDButton
          startIcon={<CloudUploadIcon />}
          fullWidth
          component="label"
          color={
            backgroundUploaded ? "success" : (!backgroundFile && !backgroundError && !!backgroundExistingPreview ? "dark" : "white")
          }
          role={undefined}
          disabled={controlsDisabled}
        >
          Fondo principal
          <VisuallyHiddenInput type="file" accept="image/*" onChange={handleBackgroundUpload} />
        </MDButton>
        <br />
        {!!backgroundUploading && (
          <MDTypography variant="caption" color="info" display="block" mt={0.5}>
            Subiendo imagen...
          </MDTypography>
        )}
        <MDTypography variant="caption" color="text">
          Tamano maximo de la imagen 2400x1600px.
        </MDTypography>
        {!!backgroundError && (
          <MDTypography
            variant="caption"
            color="error"
            display="block"
            mt={0.5}
            sx={{ whiteSpace: "pre-line" }}
          >
            {backgroundError}
          </MDTypography>
        )}
        {!!backgroundFile && !backgroundError && (
          <MDBox display="flex" alignItems="center" gap={1} mt={0.5}>
            <Chip
              size="small"
              label={backgroundUploaded ? "Subido" : "Pendiente"}
              color={backgroundUploaded ? "success" : "warning"}
              variant="outlined"
            />
            <MDTypography variant="caption" color={backgroundUploaded ? "success" : "warning"}>
              {backgroundFile.name}
            </MDTypography>
          </MDBox>
        )}
        {!backgroundFile && !backgroundError && !!backgroundExistingPreview && (
          <MDBox display="flex" alignItems="center" gap={1} mt={0.5}>
            <Chip size="small" label="Existente" color="info" variant="outlined" />
            <MDTypography variant="caption" color="info">
              Archivo existente.
            </MDTypography>
          </MDBox>
        )}
        {!!backgroundPreviewToShow && (
          <MDBox
            component="img"
            src={backgroundPreviewToShow}
            alt="Preview fondo principal"
            sx={{ mt: 1, width: "100%", maxHeight: 140, objectFit: "cover", borderRadius: 1 }}
          />
        )}
      </MDBox>

      <MDBox mt={2}>
        <MDButton
          component="label"
          role={undefined}
          color={logoUploaded ? "success" : (!logoFile && !logoError && !!logoExistingPreview ? "info" : "white")}
          fullWidth
          startIcon={<CloudUploadIcon />}
          disabled={controlsDisabled}
        >
          Logo Principal
          <VisuallyHiddenInput type="file" accept="image/*" onChange={handleLogoUpload} />
        </MDButton>
        <br />
        {!!logoUploading && (
          <MDTypography variant="caption" color="info" display="block" mt={0.5}>
            Subiendo imagen...
          </MDTypography>
        )}
        <MDTypography variant="caption" color="text">
          Tamano maximo de la imagen 960x960px.
        </MDTypography>
        {!!logoError && (
          <MDTypography
            variant="caption"
            color="error"
            display="block"
            mt={0.5}
            sx={{ whiteSpace: "pre-line" }}
          >
            {logoError}
          </MDTypography>
        )}
        {!!logoFile && !logoError && (
          <MDBox display="flex" alignItems="center" gap={1} mt={0.5}>
            <Chip
              size="small"
              label={logoUploaded ? "Subido" : "Pendiente"}
              color={logoUploaded ? "success" : "warning"}
              variant="outlined"
            />
            <MDTypography variant="caption" color={logoUploaded ? "success" : "warning"}>
              {logoFile.name}
            </MDTypography>
          </MDBox>
        )}
        {!logoFile && !logoError && !!logoExistingPreview && (
          <MDBox display="flex" alignItems="center" gap={1} mt={0.5}>
            <Chip size="small" label="Existente" color="info" variant="outlined" />
            <MDTypography variant="caption" color="info">
              Archivo existente...
            </MDTypography>
          </MDBox>
        )}
        {!!logoPreviewToShow && (
          <MDBox
            component="img"
            src={logoPreviewToShow}
            alt="Preview logo principal"
            sx={{ mt: 1, width: 80, height: 80, objectFit: "contain", borderRadius: 1 }}
          />
        )}
      </MDBox>

      <MDBox mt={2}>
        <MDButton
          component="label"
          role={undefined}
          fullWidth
          color={footerUploaded ? "success" : (!footerFile && !footerError && !!footerExistingPreview ? "secondary" : "white")}
          startIcon={<CloudUploadIcon />}
          disabled={controlsDisabled}
        >
          Footer
          <VisuallyHiddenInput type="file" accept="image/*" onChange={handleFooterUpload} />
        </MDButton>
        <br />
        {!!footerUploading && (
          <MDTypography variant="caption" color="info" display="block" mt={0.5}>
            Subiendo imagen...
          </MDTypography>
        )}
        <MDTypography variant="caption" color="text">
          Tamano maximo de la imagen 184x30px.
        </MDTypography>
        {!!footerError && (
          <MDTypography
            variant="caption"
            color="error"
            display="block"
            mt={0.5}
            sx={{ whiteSpace: "pre-line" }}
          >
            {footerError}
          </MDTypography>
        )}
        {!!footerFile && !footerError && (
          <MDBox display="flex" alignItems="center" gap={1} mt={0.5}>
            <Chip
              size="small"
              label={footerUploaded ? "Subido" : "Pendiente"}
              color={footerUploaded ? "success" : "warning"}
              variant="outlined"
            />
            <MDTypography variant="caption" color={footerUploaded ? "success" : "warning"}>
              {footerFile.name}
            </MDTypography>
          </MDBox>
        )}
        {!footerFile && !footerError && !!footerExistingPreview && (
          <MDBox display="flex" alignItems="center" gap={1} mt={0.5}>
            <Chip size="small" label="Existente" color="info" variant="outlined" />
            <MDTypography variant="caption" color="info">
              Archivo existente...
            </MDTypography>
          </MDBox>
        )}
        {!!footerPreviewToShow && (
          <MDBox
            component="img"
            src={footerPreviewToShow}
            alt="Preview footer principal"
            sx={{ mt: 1, width: 80, height: 80, objectFit: "contain", borderRadius: 1 }}
          />
        )}
      </MDBox>

      <MDBox mt={2} display="flex" gap={1}>
        <MDButton
          variant="gradient"
          color="info"
          fullWidth
          onClick={handleUploadPendingImages}
          disabled={!hasPendingUploads || controlsDisabled}
        >
          Subir
        </MDButton>
        <MDButton
          variant="outlined"
          color="info"
          fullWidth
          onClick={handleResetPendingImages}
          disabled={!hasPendingUploads || controlsDisabled}
        >
          Limpiar
        </MDButton>
      </MDBox>

      <MDBox mt={1} mb={2}>
        <MDButton
          variant="outlined"
          color="error"
          fullWidth
          onClick={handleDefaultImages}
          disabled={controlsDisabled}
        >
          {defaultResetting ? "Restaurando..." : "Restaurar"}
        </MDButton>
      </MDBox>

      {!!uploadSummary && (
        <MDTypography variant="caption" color={uploadSummaryColor} display="block" mt={1}>
          {uploadSummary}
        </MDTypography>
      )}
    </>
  )
}

export default SiteImagesSection
